import React, { useState, useEffect } from 'react';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TabNavigation from '../../components/ui/TabNavigation';
import { useAuth } from '../../contexts/AuthContext';

import { useNavigate } from 'react-router-dom';
import TutorialStep from './components/TutorialStep';
import FeatureDemo from './components/FeatureDemo';
import ProgressIndicator from './components/ProgressIndicator';
import QuickStartWizard from './components/QuickStartWizard';
import AppImage from '../../components/AppImage';
import { useTheme } from '../../contexts/ThemeContext';
import { Helmet } from 'react-helmet';

const WelcomeOnboarding = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [currentStep, setCurrentStep] = useState(0);
  const [showWizard, setShowWizard] = useState(false);
  const [completedSteps, setCompletedSteps] = useState([]);

  const tutorialSteps = [
    {
      id: 'welcome',
      title: 'Welcome to Your Starling Schedule',
      description: 'Your all-in-one family organization platform',
      icon: 'calendar-heart',
      content: {
        heading: 'Organize Your Family Life with Ease',
        points: [
          'Coordinate schedules across all family members',
          'Track daily tasks and long-term goals',
          'Color-coded system for easy identification',
          'Sync with Google Calendar and export data'
        ],
        image: 'calendar-heart'
      }
    },
    {
      id: 'calendar',
      title: 'Family Calendar View',
      description: 'See everyone\'s schedule at a glance',
      icon: 'calendar-heart',
      content: {
        heading: 'Master Your Family Calendar',
        points: [
          'Switch between month, week, and day views',
          'Add activities with member assignments',
          'Set reminders and recurring events',
          'Filter by family member using color codes',
          'View public holidays and school terms'
        ],
        features: [
          { label: 'Color Coding', description: 'Each member has a unique color for easy identification' },
          { label: 'Recurring Events', description: 'Set up weekly classes, monthly meetings, and more' },
          { label: 'Reminders', description: 'Get notified before important events' }
        ]
      }
    },
    {
      id: 'daily-tasks',
      title: 'Daily Task Management',
      description: 'Organize your day hour by hour',
      icon: 'calendar-heart',
      content: {
        heading: 'Time-Slotted Daily Planning',
        points: [
          'View tasks in hourly time slots (6 AM - 11 PM)',
          'Drag and drop tasks to reschedule',
          'Assign tasks to specific family members',
          'Track task completion with checkboxes',
          'See weather information for planning'
        ],
        features: [
          { label: 'Time Blocks', description: 'Organize tasks into specific time slots' },
          { label: 'Member Assignment', description: 'Assign tasks to one or multiple members' },
          { label: 'Quick Add', description: 'Use the + button for fast task creation' }
        ]
      }
    },
    {
      id: 'long-term',
      title: 'Long Term Task Planning',
      description: 'Track goals and projects over time',
      icon: 'calendar-heart',
      content: {
        heading: 'Project & Goal Management',
        points: [
          'Create tasks with priorities (High, Medium, Low)',
          'Set due dates and track progress',
          'Organize by status: To Do, In Progress, Completed',
          'Filter by member, priority, or category',
          'Add detailed descriptions and notes'
        ],
        features: [
          { label: 'Priority Levels', description: 'Mark urgent tasks with high priority' },
          { label: 'Status Tracking', description: 'Move tasks through workflow stages' },
          { label: 'Categories', description: 'Organize by Work, Personal, School, Health, etc.' }
        ]
      }
    },
    {
      id: 'settings',
      title: 'Family Settings',
      description: 'Customize your family profile',
      icon: 'calendar-heart',
      content: {
        heading: 'Personalize Your Experience',
        points: [
          'Add family members with custom colors',
          'Set notification preferences',
          'Connect Google Calendar for sync',
          'Configure holiday and school term display',
          'Enable parental lock for sensitive settings',
          'Track achievements and rewards'
        ],
        features: [
          { label: 'Member Colors', description: 'Assign unique colors to each family member' },
          { label: 'Sync Options', description: 'Two-way sync with Google Calendar' },
          { label: 'Backup & Restore', description: 'Export and import your family data' }
        ]
      }
    },
    {
      id: 'tips',
      title: 'Pro Tips & Shortcuts',
      description: 'Get the most out of Starling Family Planner',
      icon: 'calendar-heart',
      content: {
        heading: 'Expert Tips for Power Users',
        points: [
          'Use the quick action button (bottom right) for fast task creation',
          'Click on any calendar date to add activities instantly',
          'Filter views by member to focus on individual schedules',
          'Export data regularly for backup purposes',
          'Set up recurring events for weekly activities',
          'Use achievements to motivate kids with task completion'
        ],
        features: [
          { label: 'Keyboard Shortcuts', description: 'Navigate faster with keyboard commands' },
          { label: 'Bulk Actions', description: 'Select multiple tasks for batch operations' },
          { label: 'Smart Filters', description: 'Combine filters for precise views' }
        ]
      }
    }
  ];

  const handleNext = () => {
    if (!completedSteps?.includes(currentStep)) {
      setCompletedSteps([...completedSteps, currentStep]);
    }
    if (currentStep < tutorialSteps?.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    window.location.href = '/family-calendar-view';
  };

  const handleStepClick = (index) => {
    setCurrentStep(index);
  };

  const handleStartWizard = () => {
    setShowWizard(true);
  };

  const handleWizardComplete = () => {
    setShowWizard(false);
    window.location.href = '/family-calendar-view';
  };

  const currentTutorial = tutorialSteps?.[currentStep];
  const isLastStep = currentStep === tutorialSteps?.length - 1;

  return (
    <>
      <Helmet>
        <title>Welcome - Starling Schedule</title>
      </Helmet>
      <div className={`min-h-screen ${
        isDarkMode 
          ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' :'bg-gradient-to-br from-blue-50 via-violet-50 to-pink-50'
      }`}>
        <TabNavigation />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {!showWizard ? (
            <>
              {/* Header */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center mb-4">
                  <AppImage 
                    src="/assets/images/ad4cbaf4-dc2b-4059-a1d5-7af12fa38f76_copy-1767800870676.jpg" 
                    alt="Starling Family Planner Logo" 
                    className="w-32 h-32 object-contain"
                  />
                </div>
                <h1 className="text-h1 font-heading text-foreground mb-2">Welcome to Your Starling Schedule</h1>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Learn how to use all features to keep your family organized and connected
                </p>
              </div>

              {/* Progress Indicator */}
              <ProgressIndicator
                steps={tutorialSteps}
                currentStep={currentStep}
                completedSteps={completedSteps}
                onStepClick={handleStepClick}
              />

              {/* Tutorial Content */}
              <div className="bg-card rounded-xl shadow-warm-lg p-8 mb-8">
                <TutorialStep tutorial={currentTutorial} />
                
                {currentStep > 0 && (
                  <FeatureDemo features={currentTutorial?.content?.features} />
                )}
              </div>

              {/* Navigation Controls */}
              <div className="flex items-center justify-between mb-8">
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={currentStep === 0}
                  iconName="chevron-left"
                  iconPosition="left"
                >
                  Previous
                </Button>

                <div className="flex gap-3">
                  <Button
                    variant="ghost"
                    onClick={handleSkip}
                  >
                    Skip Tutorial
                  </Button>
                  
                  {isLastStep ? (
                    <Button
                      variant="success"
                      onClick={handleStartWizard}
                      iconName="rocket"
                      iconPosition="right"
                    >
                      Start Quick Setup
                    </Button>
                  ) : (
                    <Button
                      onClick={handleNext}
                      iconName="chevron-right"
                      iconPosition="right"
                    >
                      Next
                    </Button>
                  )}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-card rounded-lg shadow-warm-md p-6 hover:shadow-warm-lg transition-shadow cursor-pointer" onClick={() => window.location.href = '/family-calendar-view'}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                      <Icon name="calendar" size={20} className="text-blue-600 dark:text-blue-400" />
                    </div>
                    <h3 className="text-h5 font-heading text-foreground">Family Calendar</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">
                    View and manage all family activities in one place
                  </p>
                  <ul className="text-xs text-muted-foreground space-y-1 mb-4">
                    <li>• Switch between month, week, and day views</li>
                    <li>• Color-coded by family member</li>
                    <li>• Set reminders and recurring events</li>
                  </ul>
                  <Button
                    variant="outline"
                    size="sm"
                    fullWidth
                    iconName="arrow-right"
                    iconPosition="right"
                  >
                    Go to Calendar
                  </Button>
                </div>

                <div className="bg-card rounded-lg shadow-warm-md p-6 hover:shadow-warm-lg transition-shadow cursor-pointer" onClick={() => window.location.href = '/daily-task-management'}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-violet-100 dark:bg-violet-900/30 rounded-lg flex items-center justify-center">
                      <Icon name="clock" size={20} className="text-violet-600 dark:text-violet-400" />
                    </div>
                    <h3 className="text-h5 font-heading text-foreground">Daily Tasks</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">
                    Organize your day with time-slotted task management
                  </p>
                  <ul className="text-xs text-muted-foreground space-y-1 mb-4">
                    <li>• Hourly time slots from 6 AM to 10 PM</li>
                    <li>• Assign tasks to family members</li>
                    <li>• Track completion with checkboxes</li>
                  </ul>
                  <Button
                    variant="outline"
                    size="sm"
                    fullWidth
                    iconName="arrow-right"
                    iconPosition="right"
                  >
                    Go to Daily Tasks
                  </Button>
                </div>

                <div className="bg-card rounded-lg shadow-warm-md p-6 hover:shadow-warm-lg transition-shadow cursor-pointer" onClick={() => window.location.href = '/long-term-task-planning'}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center">
                      <Icon name="list-checks" size={20} className="text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <h3 className="text-h5 font-heading text-foreground">Long Term Tasks</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">
                    Track goals and projects with priority management
                  </p>
                  <ul className="text-xs text-muted-foreground space-y-1 mb-4">
                    <li>• Set priorities (High, Medium, Low)</li>
                    <li>• Organize by status and category</li>
                    <li>• Filter by member and due date</li>
                  </ul>
                  <Button
                    variant="outline"
                    size="sm"
                    fullWidth
                    iconName="arrow-right"
                    iconPosition="right"
                  >
                    Go to Long Term Tasks
                  </Button>
                </div>

                <div className="bg-card rounded-lg shadow-warm-md p-6 hover:shadow-warm-lg transition-shadow cursor-pointer" onClick={() => window.location.href = '/family-settings-management'}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
                      <Icon name="settings" size={20} className="text-amber-600 dark:text-amber-400" />
                    </div>
                    <h3 className="text-h5 font-heading text-foreground">Family Settings</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">
                    Customize your family profile and preferences
                  </p>
                  <ul className="text-xs text-muted-foreground space-y-1 mb-4">
                    <li>• Add family members with custom colors</li>
                    <li>• Configure notifications and sync</li>
                    <li>• Enable parental lock and achievements</li>
                  </ul>
                  <Button
                    variant="outline"
                    size="sm"
                    fullWidth
                    iconName="arrow-right"
                    iconPosition="right"
                  >
                    Go to Settings
                  </Button>
                </div>

                <div className="bg-card rounded-lg shadow-warm-md p-6 hover:shadow-warm-lg transition-shadow cursor-pointer" onClick={() => window.location.href = '/family-messaging-interface'}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-pink-100 dark:bg-pink-900/30 rounded-lg flex items-center justify-center">
                      <Icon name="message-circle" size={20} className="text-pink-600 dark:text-pink-400" />
                    </div>
                    <h3 className="text-h5 font-heading text-foreground">Family Messages</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">
                    Communicate with family members and share lists
                  </p>
                  <ul className="text-xs text-muted-foreground space-y-1 mb-4">
                    <li>• Group and individual conversations</li>
                    <li>• Share grocery and shopping lists</li>
                    <li>• Quick message templates</li>
                  </ul>
                  <Button
                    variant="outline"
                    size="sm"
                    fullWidth
                    iconName="arrow-right"
                    iconPosition="right"
                  >
                    Go to Messages
                  </Button>
                </div>

                <div className="bg-card rounded-lg shadow-warm-md p-6 hover:shadow-warm-lg transition-shadow cursor-pointer" onClick={() => window.location.href = '/weekly-menu-planning'}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
                      <Icon name="utensils" size={20} className="text-orange-600 dark:text-orange-400" />
                    </div>
                    <h3 className="text-h5 font-heading text-foreground">Menu Planning</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">
                    Plan weekly meals and generate grocery lists
                  </p>
                  <ul className="text-xs text-muted-foreground space-y-1 mb-4">
                    <li>• Weekly meal grid with breakfast to dinner</li>
                    <li>• Meal library with recipes</li>
                    <li>• Auto-generate grocery lists</li>
                  </ul>
                  <Button
                    variant="outline"
                    size="sm"
                    fullWidth
                    iconName="arrow-right"
                    iconPosition="right"
                  >
                    Go to Menu Planning
                  </Button>
                </div>

                <div className="bg-card rounded-lg shadow-warm-md p-6 hover:shadow-warm-lg transition-shadow cursor-pointer" onClick={handleStartWizard}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center">
                      <Icon name="rocket" size={20} className="text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <h3 className="text-h5 font-heading text-foreground">Quick Start Wizard</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">
                    Get started quickly with our guided setup
                  </p>
                  <ul className="text-xs text-muted-foreground space-y-1 mb-4">
                    <li>• Add your family members</li>
                    <li>• Set up color preferences</li>
                    <li>• Configure initial settings</li>
                  </ul>
                  <Button
                    variant="outline"
                    size="sm"
                    fullWidth
                    iconName="arrow-right"
                    iconPosition="right"
                  >
                    Launch Wizard
                  </Button>
                </div>

                <div className="bg-card rounded-lg shadow-warm-md p-6 hover:shadow-warm-lg transition-shadow cursor-pointer" onClick={() => window.location.href = '/faq-help-center'}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-teal-100 dark:bg-teal-900/30 rounded-lg flex items-center justify-center">
                      <Icon name="help-circle" size={20} className="text-teal-600 dark:text-teal-400" />
                    </div>
                    <h3 className="text-h5 font-heading text-foreground">Help Center</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">
                    Browse FAQs and troubleshooting guides
                  </p>
                  <ul className="text-xs text-muted-foreground space-y-1 mb-4">
                    <li>• Searchable FAQ categories</li>
                    <li>• Step-by-step troubleshooting</li>
                    <li>• Contact support options</li>
                  </ul>
                  <Button
                    variant="outline"
                    size="sm"
                    fullWidth
                    iconName="arrow-right"
                    iconPosition="right"
                  >
                    Visit Help Center
                  </Button>
                </div>

                <div className="bg-card rounded-lg shadow-warm-md p-6 hover:shadow-warm-lg transition-shadow cursor-pointer" onClick={() => window.location.href = '/individual-profile-management'}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                      <Icon name="user" size={20} className="text-purple-600 dark:text-purple-400" />
                    </div>
                    <h3 className="text-h5 font-heading text-foreground">My Profile</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">
                    Manage your personal information and preferences
                  </p>
                  <ul className="text-xs text-muted-foreground space-y-1 mb-4">
                    <li>• Update personal details</li>
                    <li>• Set notification preferences</li>
                    <li>• Customize display settings</li>
                  </ul>
                  <Button
                    variant="outline"
                    size="sm"
                    fullWidth
                    iconName="arrow-right"
                    iconPosition="right"
                  >
                    Go to Profile
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <QuickStartWizard onComplete={handleWizardComplete} onSkip={handleSkip} />
          )}
        </div>
      </div>
    </>
  );
};

export default WelcomeOnboarding;